import { Text } from '@daily/shared/components/Text';
import { Tooltip } from '@daily/shared/components/Tooltip';
import {
  useLocalParticipant,
  useParticipant,
} from '@daily-co/daily-react-hooks';
import React, { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useResizeObserver } from '../../hooks/useResizeObserver';
import { useStyleVariants } from '../../hooks/useStyleVariants';
import { ParticipantActions } from './ParticipantActions';

interface Props {
  iconVariant: ReturnType<typeof useStyleVariants>['iconVariant'];
  localParticipant: ReturnType<typeof useLocalParticipant>;
  showActions?: boolean;
  sessionId: string;
  showMicMutedState: boolean;
  textVariant: ReturnType<typeof useStyleVariants>['textVariant'];
}

export const ParticipantRow: React.FC<Props> = memo(
  ({
    iconVariant,
    localParticipant,
    showActions = false,
    sessionId,
    showMicMutedState,
    textVariant,
  }) => {
    const { t } = useTranslation();
    const nameRef = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const participant = useParticipant(sessionId);

    useResizeObserver(
      nameRef,
      useCallback(() => {
        if (!nameRef.current) return;
        const p = nameRef.current.querySelector('p');
        setIsTruncated(p?.scrollWidth > p?.clientWidth);
      }, [])
    );

    if (!participant) return null;

    return (
      <div className="participant-row">
        <Tooltip
          disabled={!isTruncated}
          id={`tt-${sessionId}`}
          title={participant.user_name}
        >
          <div ref={nameRef}>
            <Text truncate variant={textVariant}>
              {participant.local
                ? participant.user_name
                  ? `${t('people.you')} (${participant.user_name})`
                  : t('people.youGuest')
                : participant.user_name
                ? participant.user_name
                : t('people.guest')}
            </Text>
          </div>
        </Tooltip>
        {showActions && (
          <div className="actions">
            <ParticipantActions
              iconVariant={iconVariant}
              localParticipant={localParticipant}
              participant={participant}
              sessionId={sessionId}
              showMicMutedState={showMicMutedState}
            />
          </div>
        )}
        <style jsx>{`
          .participant-row {
            align-items: center;
            display: flex;
            gap: 8px;
            justify-content: space-between;
          }
          .actions {
            flex: none;
          }
        `}</style>
      </div>
    );
  }
);
ParticipantRow.displayName = 'ParticipantRow';
